/**
 * 표시용 포맷터 — **화면마다 다시 만들지 않는다.**
 *
 * ★ 2026-09-09 에 `packages/ui` 에서 여기로 옮겼다(0043). 서버가 PDF 명세서에 같은
 *   금액·같은 연월을 그려야 하는데, 서버는 `@worhythm/ui` 를 못 쓴다(React 가 딸려 온다)
 *   고 `@worhythm/ui` 는 `@worhythm/core` 를 못 쓴다(급여 계산기가 웹 번들에 들어간다).
 *   두 당사자의 유일한 공통 바닥이 contracts 다 — `FILE_PREFIX` 를 여기 둔 논거와 같다.
 *   `@worhythm/ui` 는 그대로 재수출하므로 화면의 import 는 한 줄도 안 바뀐다.
 *
 * 구 stationon-erp 에는 금액 포맷이 프런트 여러 곳에 흩어져 있었고, 그래서 같은 금액이
 * 화면마다 `3,000,000` · `3000000` · `₩3,000,000` 으로 보였다. 포맷이 다르면 사용자는
 * 같은 숫자인지 매번 다시 확인한다.
 *
 * 로케일을 인자로 받지 않는다. 이 제품의 로케일은 오늘 `ko` 하나이고(`LOCALES`),
 * 늘어나는 순간 **모든 모듈이 컴파일에 실패하게** 되어 있다 — 그때 이 함수도 같이 고친다.
 * 미리 인자를 열어 두면 호출자마다 다른 로케일을 넘길 수 있게 되고, 그건 지금 없는 문제다.
 */
import { PRODUCT_TZ } from './jobs.js';

const KRW = new Intl.NumberFormat('ko-KR');

/**
 * 원 단위 금액. **통화 기호를 붙이지 않는다** — 표의 열 제목이 이미 "지급총액"이고,
 * 칸마다 ₩ 가 반복되면 숫자를 비교하기 어려워진다. 단위가 필요한 자리는 제목이 말한다.
 */
export const formatMoney = (won: number): string => KRW.format(won);

/** '2026-09' → '2026년 9월'. 급여의 단위는 달이라 날짜 포맷을 쓰지 않는다. */
export function formatYearMonth(yearMonth: string): string {
  const [y, m] = yearMonth.split('-');
  // 형식이 아니면 **그대로 돌려준다.** 여기서 던지면 목록 한 줄 때문에 화면 전체가 죽는다.
  if (!y || !m) return yearMonth;
  return `${y}년 ${Number(m)}월`;
}

/* ══════════════════════════════════════════════════════════════════════════
   날짜·시각 — **제품 전체가 한 모양이다** (0156)
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * 사람이 읽는 시각의 **유일한 모양**. (2026-09-21, 소유자 지시)
 *
 *   시각까지: `2026/09/21(월) 14:55`
 *   날짜만:   `2026/09/21(월)`
 *
 * ─── 왜 여기 한 곳인가 ───
 * 2026-09-21 까지 이 리포에는 시각을 만드는 자리가 **세 종류**였다:
 * 이 함수(`2026-09-01 03:00`) · RG 화면 전부가 쓰던 `toLocaleString('ko-KR')`
 * (`2026. 9. 21. 오후 2:55:03`) · 근태 카드의 `toLocaleTimeString()`(로케일도 안 줬다).
 * 같은 사실이 화면마다 다른 모양이면 사람은 **같은 시각인지 매번 다시 읽는다** —
 * 금액에서 이미 내린 판단과 같다(이 파일 머리의 그 문단).
 *
 * ─── 요일이 왜 들어가나 ───
 * 창고·근태 화면의 질문 절반이 *"그게 무슨 요일이었나"* 다(출고 마감·주말 근무·휴가).
 * 날짜에서 요일을 암산하는 비용이 괄호 넷보다 비싸다.
 *
 * ─── 초는 안 쓴다 ───
 * `toLocaleString` 은 초까지 찍었다. 이 제품에서 초가 판단을 바꾸는 화면은 없고,
 * 표에서 그 세 글자가 열 폭을 끌어올린다.
 *
 * ─── 시간대는 상수다 ───
 * 브라우저 로케일에 맡기면 같은 실행이 사람마다 **다른 날짜**로 보인다. 잡은 매월 1일
 * 03:00 KST 에 도는데 그 순간의 UTC 는 전달 말일 18:00 이다 — 그 화면에서 하는 판단이
 * "이 잡이 이번 달에 돌았는가"다.
 */
const DATE_PARTS = new Intl.DateTimeFormat('ko-KR', {
  timeZone: PRODUCT_TZ, hour12: false,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', weekday: 'short',
});

/** 값이 없는 칸. **빈 문자열이 아니다** — 빈 칸은 고장으로 읽히고, 이건 "아직 없다"는 사실이다. */
export const ABSENT = '—';

/** 시각 하나를 조각으로 푼다. 파싱이 안 되면 `null` — 부르는 쪽이 원문을 그대로 낸다. */
function partsOf(v: string | Date): Record<string, string> | null {
  const at = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(at.getTime())) return null;
  const p: Record<string, string> = {};
  for (const part of DATE_PARTS.formatToParts(at)) if (part.type !== 'literal') p[part.type] = part.value;
  return p;
}

/** `2026/09/21(월)`. 날짜만 필요한 칸 — 시각이 판단을 안 바꾸는 자리다. */
export function formatDate(v: string | Date | null | undefined): string {
  if (v === null || v === undefined || v === '') return ABSENT;
  const p = partsOf(v);
  /* 파싱 실패는 **원문을 낸다.** 한 줄 때문에 화면 전체가 죽지 않게(금액과 같은 규약). */
  if (p === null) return typeof v === 'string' ? v : ABSENT;
  return `${p.year}/${p.month}/${p.day}(${p.weekday})`;
}

/** `2026/09/21(월) 14:55`. */
export function formatDateTime(v: string | Date | null | undefined): string {
  if (v === null || v === undefined || v === '') return ABSENT;
  const p = partsOf(v);
  if (p === null) return typeof v === 'string' ? v : ABSENT;
  /* `hour12: false` 가 자정을 `24` 로 주는 환경이 있다 — 그 하루는 `00` 이다. */
  const hh = String(Number(p.hour) % 24).padStart(2, '0');
  return `${p.year}/${p.month}/${p.day}(${p.weekday}) ${hh}:${p.minute}`;
}

/**
 * `14:55`. **같은 날 안에서만** 쓴다 — 근태 카드처럼 날짜가 화면의 다른 곳에 이미 있는 자리.
 * 날짜가 없는 화면에서 이것을 쓰면 "어제 것인가"를 답할 수 없다.
 */
export function formatClock(v: string | Date | null | undefined): string {
  if (v === null || v === undefined || v === '') return ABSENT;
  const p = partsOf(v);
  if (p === null) return typeof v === 'string' ? v : ABSENT;
  return `${String(Number(p.hour) % 24).padStart(2, '0')}:${p.minute}`;
}

/**
 * **오늘**(`2026-09-21`) — 제품의 하루 기준. **표시용이 아니라 기계 키다**(비교·URL·조회).
 *
 * `new Date().toLocaleDateString('en-CA')` 를 쓰면 **브라우저의 오늘**이 나온다. 그 함정은
 * 이 리포가 이미 서버에서 밟았다(`docs/status.md` §4 의 `current_date` 줄: CI 가 UTC 라
 * 15시 이후에만 빨개졌다). 화면에서도 같은 갈래가 있다 — 해외에서 접속한 사람의 '오늘'이
 * 창고의 '오늘'과 다르면 그 화면의 '다음 날' 버튼이 하루 어긋난다.
 */
export function todayInProductTz(now: Date = new Date()): string {
  const p = partsOf(now);
  return p === null ? '' : `${p.year}-${p.month}-${p.day}`;
}

/** `3, 0` → `03:00`. 주기 문장이 쓴다 — 시각은 선언의 숫자 둘이지 시각 객체가 아니다. */
export const formatTimeOfDay = (hour: number, minute: number): string =>
  `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
